import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { MaskPass, ClearMaskPass } from 'three/examples/jsm/postprocessing/MaskPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { Sizes } from './Sizes';
import { Scene } from './Scene';
import { Camera } from './Camera';
import Time from './Time';
import EventEmitter from '../Utils/EventEmitter';
import { ClearPass } from 'three/examples/jsm/postprocessing/ClearPass.js';
import vertexShader from '../shaders/postprocessingSectionTwo/vertex.glsl';
import fragmentShader from '../shaders/postprocessingSectionTwo/fragment.glsl';

// Define the custom GrainColorShader
const GrainColorShader = {
  uniforms: {
    'tDiffuse': { value: null },
    'uGrainSize': { value: 2 },
    'uResolution': { value: new THREE.Vector2(1, 1) },
    'uRadialEllipse': { value: null },
    'uRadialEllipseSize': { value: 0.5 },
    'uGrainColor': { value: new THREE.Color(0x3d8be8) }, //3d8be8 // 00D4FF
  },
  vertexShader,
  fragmentShader
};

/**
 * Renderer class
 * Manages the THREE.js renderer
 */
export class Renderer extends EventEmitter {
  // Static instance property
  static instance = null;

  /**
   * Get the singleton instance of the class
   * @returns {Renderer} The singleton instance
   */
  static getInstance(parent) {
    if (!Renderer.instance) {
      Renderer.instance = new Renderer(parent);
    }
    return Renderer.instance;
  }

  /**
   * Constructor
   */
  constructor(parent) {
    // Ensure this is a singleton
    if (Renderer.instance) {
      return Renderer.instance;
    }

    super();
    this.parent = parent;
    this.sizes = Sizes.getInstance();
    this.scene = Scene.getInstance();
    this.camera = Camera.getInstance();
    this.time = Time.getInstance();

    this.enableMSAA = false;

    // Layer for SectionTwo objects
    this.GRAIN_LAYER = 2;

    // Post-processing enabled flag
    this.postProcessingEnabled = true;

    // Create renderer
    this.createRenderer();

    // Initialize post-processing
    this.initPostProcessing();

    // Set up event listeners
    this.setupEventListeners();

    // Set instance
    Renderer.instance = this;
  }

  /**
   * Create WebGL renderer
   */
  createRenderer() {
    this.instance = new THREE.WebGLRenderer({
      alpha: false,
      antialias: false,
    })

    this.wrapper = document.getElementById('smooth-wrapper');

    this.instance.shadowMap.enabled = false;

    this.instance.domElement.classList.add('game-theory-canvas');
    this.canvas = this.wrapper.insertBefore(this.instance.domElement, this.wrapper.firstChild);

    // Configure renderer
    this.instance.setSize(this.sizes.width, this.sizes.height);
    this.instance.setPixelRatio(Math.min(this.sizes.pixelRatio, 2));
    this.instance.setClearColor(0x000000, 0);
    this.instance.autoClear = false;

    // Create a render target with stencil buffer and antialiasing
    const renderTargetParams = {
      stencilBuffer: true,
      //type: THREE.HalfFloatType,
    };
    
    // Add MSAA if supported (WebGL 2)
    if (this.instance.capabilities.isWebGL2 && this.enableMSAA) {
      renderTargetParams.samples = 2;
    }
    
    this.renderTarget = new THREE.WebGLRenderTarget(
      this.sizes.width * this.sizes.pixelRatio,
      this.sizes.height * this.sizes.pixelRatio,
      renderTargetParams
    );

    this.trigger('created');
  }

  /**
   * Initialize post-processing
   */
  initPostProcessing() {

    const clearPass = new ClearPass();
    const outputPass = new OutputPass();
    const clearMaskPass = new ClearMaskPass();

    this.camera.maskCamera.layers.set(this.GRAIN_LAYER);
  
    this.composer = new EffectComposer(this.instance, this.renderTarget);
    

    this.composer.addPass(clearPass);
    const renderPass = new RenderPass(this.scene.instance, this.camera.instance);
    this.composer.addPass(renderPass);

    // Create mask pass for SectionTwo
    this.maskPass = new MaskPass(this.scene.instance, this.camera.maskCamera);
    this.composer.addPass(this.maskPass);
  

    // Create grain pass
    this.grainPass = new ShaderPass(GrainColorShader);
    this.grainPass.material.uniforms.uResolution.value.set(
      this.sizes.width,
      this.sizes.height
    );

    this.composer.addPass(this.grainPass);

    this.composer.addPass(clearMaskPass);

    this.composer.addPass(outputPass);
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Handle resize
    this.sizes.on('resize', this.onResize.bind(this));

  }

  /**
   * Handle resize event
   */
  onResize() {
    // Update renderer size
    this.instance.setSize(this.sizes.width, this.sizes.height);
    this.instance.setPixelRatio(Math.min(this.sizes.pixelRatio, 2));

    // Update composer size
    if (this.composer) {
      this.composer.setSize(this.sizes.width * this.sizes.pixelRatio, this.sizes.height * this.sizes.pixelRatio);
      
      // Update grain resolution
      if (this.grainPass) {
        this.grainPass.material.uniforms.uResolution.value.set(
          this.sizes.width * this.sizes.pixelRatio,
          this.sizes.height * this.sizes.pixelRatio
        );
      }
    }

    // Trigger resized event
    this.trigger('resized');
  }

  render() {
    if (this.postProcessingEnabled && this.composer) {
      // Render with post-processing
      this.composer.render();
    } else {
      // Render without post-processing
      this.instance.render(this.scene.instance, this.camera.instance);
    }
  }

  /**
   * Enable grain effect for a specific object
   * @param {THREE.Object3D} object - The object to apply the effect to
   */
  enableGrainForObject(object) {
    object.layers.enable(this.GRAIN_LAYER);
  }

  /**
   * Disable grain effect for a specific object
   * @param {THREE.Object3D} object - The object to remove the effect from
   */
  disableGrainForObject(object) {
    object.layers.disable(this.GRAIN_LAYER);
  }

  /**
   * Configure grain effect parameters
   * @param {Object} params - Grain effect parameters
   * @param {Number} params.grainAmount - Amount of grain (0-1)
   * @param {Number} params.grainScale - Scale of grain
   * @param {Number} params.brightness - Brightness adjustment (-1 to 1)
   * @param {Number} params.contrast - Contrast adjustment (0-2)
   * @param {Number} params.saturation - Saturation adjustment (0-2)
   */
  configureGrainEffect(params = {}) {
    if (!this.grainPass) return;
    
    const uniforms = this.grainPass.material.uniforms;
    
    if (params.uGrainAmount !== undefined) uniforms.uGrainAmount.value = params.uGrainAmount;
    if (params.uGrainSize !== undefined) uniforms.uGrainSize.value = params.uGrainSize;
    if (params.uLumAmount !== undefined) uniforms.uLumAmount.value = params.uLumAmount;
    if (params.uTimer !== undefined) uniforms.uTimer.value = params.uTimer;
    if (params.uResolution !== undefined) uniforms.uResolution.value = params.uResolution;
    if (params.uRadialEllipse !== undefined) uniforms.uRadialEllipse.value = params.uRadialEllipse;
    if (params.uRadialEllipseSize !== undefined) uniforms.uRadialEllipseSize.value = params.uRadialEllipseSize;
  }

  /**
   * Toggle post-processing
   * @param {Boolean} enabled - Whether post-processing should be enabled
   */
  setPostProcessingEnabled(enabled) {
    this.postProcessingEnabled = enabled;
  }

  /**
   * Set renderer clear color
   * @param {String|Number} color - Color value
   * @param {Number} alpha - Alpha value (0-1)
   */
  setClearColor(color, alpha = 1) {
    this.instance.setClearColor(color, alpha);
    this.trigger('clearColorChanged', [{ color, alpha }]);
  }

  /**
   * Set renderer pixel ratio
   * @param {Number} ratio - Pixel ratio
   */
  setPixelRatio(ratio) {
    this.instance.setPixelRatio(Math.min(ratio, 2));
    
    // Update composer pixel ratio if exists
    if (this.composer) {
      this.composer.setPixelRatio(Math.min(ratio, 2));
      
      // Update grain resolution
      if (this.grainPass) {
        this.grainPass.material.uniforms.uResolution.value.set(
          this.sizes.width * this.sizes.pixelRatio,
          this.sizes.height * this.sizes.pixelRatio
        );
      }
    }
    
    this.trigger('pixelRatioChanged', [ratio]);
  }

  /**
   * Dispose renderer and clean up resources
   */
  dispose() {
    // Remove event listeners
    this.sizes.off('resize', this.onResize);
    this.time.off('tick', this.onTick);

    // Dispose renderer
    if (this.instance) {
      this.instance.dispose();
    }
    
    // Dispose composer and passes
    if (this.composer) {
      this.composer.dispose();
    }

    // Trigger disposed event
    this.trigger('disposed');
  }
}
